import React, { CSSProperties } from 'react';
import { Table } from 'react-bootstrap';

import { formatNumberBR } from 'components/Relatorios/shared/RelatorioTable';
import { corDoPreco, ehPrecoZerado, escalaDaColuna } from 'helper/Relatorios/Dessem/precosHeatmap';
import { DessemPrecoLinha, DessemResultadosPrecos } from 'types/Relatorios/Dessem';

import './PrecosTable.scss';

interface Props { precos: DessemResultadosPrecos; }

/**
 * Tabela de Preços do DESSEM: header de 3 níveis (Contingência → D-1 Limites →
 * Subsistema). `RelatorioTable` (shared) só suporta header plano — cabeçalho
 * agrupado (colspan) precisa de `<Table>` própria dentro do wrapper
 * `.relatorio-table`, mesmo padrão do Preços do DECOMP
 * (`Decomp/secoes/Resultados`) e do `GeracaoRenovavelUnsi`. O Card fica a
 * cargo do componente pai (`Resultados`), que agrupa todas as secoes em um
 * unico container.
 *
 * Cada coluna (contingência × limite × subsistema) é um heatmap próprio: escala divergente Pastel
 * com o neutro na média da coluna — responde "esse preço está acima ou abaixo do normal da
 * coluna?". Zerados (≈0) ficam em cinza, fora da escala
 * (ver `helper/Relatorios/Dessem/precosHeatmap`).
 */
export default function PrecosTable({ precos }: Props) {
  const {
    contingencias, limites, subsistemas, linhas,
  } = precos;
  const colSpanGrupo = limites.length * subsistemas.length;
  const colunas = contingencias.flatMap((contingencia) => limites.flatMap((limite) => (
    subsistemas.map((subsistema) => {
      const valorDe = (linha: DessemPrecoLinha) => linha.valores[contingencia][limite][subsistema];
      return {
        key: `${contingencia}-${limite}-${subsistema}`,
        valorDe,
        escala: escalaDaColuna(linhas.map(valorDe)),
      };
    })
  )));

  return (
    <div className="relatorio-table dessem-precos">
      <Table responsive hover>
        <thead>
          <tr>
            <th rowSpan={3}>Hora</th>
            {contingencias.map((contingencia) => (
              <th key={contingencia} colSpan={colSpanGrupo}>{contingencia}</th>
            ))}
          </tr>
          <tr>
            {contingencias.map((contingencia) => (
              limites.map((limite) => (
                <th key={`${contingencia}-${limite}`} colSpan={subsistemas.length}>{limite}</th>
              ))
            ))}
          </tr>
          <tr>
            {contingencias.map((contingencia) => (
              limites.map((limite) => (
                subsistemas.map((subsistema) => (
                  <th key={`${contingencia}-${limite}-${subsistema}`}>{subsistema}</th>
                ))
              ))
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <tr key={linha.hora}>
              <td>{linha.hora}</td>
              {colunas.map(({ key, valorDe, escala }) => {
                const valor = valorDe(linha);
                if (escala === null || !Number.isFinite(valor) || ehPrecoZerado(valor)) {
                  return <td key={key} className="dessem-precos__zerado">{formatNumberBR(valor)}</td>;
                }
                return (
                  <td
                    key={key}
                    className="dessem-precos__celula"
                    style={{ '--dessem-preco-bg': corDoPreco(valor, escala) } as CSSProperties}
                  >
                    {formatNumberBR(valor)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
